import { useEffect, useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { ErrorText } from "../../StyledCss/styledComponents";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
  StyledSelect,
  StyledSelectContainer,
  StyledSelectGroup,
} from "../../StyledCss/styledInputs";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import {
  getAllActiveEmployees,
  getProjectModules,
} from "../../api/services/hroneApi";
import CustomMultiSelect from "../CustomMultiSelect";

import "./addUserForm.css";

const AddProjectAssignmentForm = ({
  onSubmit,
  onCancel,
  selectedProjectId,
  editData = null,
  isLoading,
  projectStartDate,
  errorMsg,
  onClearError
}) => {
  const isEditMode = useMemo(() => !!editData, [editData]);

  const [employeeId, setEmployeeId] = useState("");
  const [employeeIdError, setEmployeeIdError] = useState(false);
  const [assignedAt, setAssignedAt] = useState("");
  const [startDateError, setStartDateError] = useState(false);
  const [projectExperience, setProjectExperience] = useState("");
  const [projectExperienceError, setProjectExperienceError] = useState(false);
  const [billingAmount, setBillingAmount] = useState("");
  const [billingAmountError, setBillingAmountError] = useState(false);
  const [billingType, setBillingType] = useState("");
  const [serviceType, setServiceType] = useState("");

  const [billingTypeError, setBillingTypeError] = useState(false);
  const [serviceTypeError, setServiceTypeError] = useState(false);

  const [moduleIds, setModuleIds] = useState([]);
  const [empId, setEmpId] = useState([]);
  const [modules, setModules] = useState([]);

  // Populate form fields when in edit mode
  useEffect(() => {
    if (isEditMode && editData) {
      setEmployeeId(editData.employeeId || "");
      setAssignedAt(editData.assignedAt.split(" ")[0] || "");
      setProjectExperience(editData.projectExperience || "");
      setBillingAmount(editData.billingAmount || "");
      setBillingType(editData.billingType || "");
      setModuleIds(editData.modules ? editData.modules.map(module => module.id) : []);
      setServiceType(editData.serviceType || "");
    }
  }, [isEditMode, editData]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await getAllActiveEmployees();
      if (response && response.ok) {
        const employees = await response.json();
        setEmpId(employees);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to fetch employees");
    }
  }, []);

  const fetchModules = useCallback(async () => {
    try {
      const response = await getProjectModules(selectedProjectId);
      if (response && response.ok) {
        const modulesList = await response.json();
        setModules(Array.isArray(modulesList) ? modulesList : []);
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      setModules([]);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchEmployees();
    fetchModules();
  }, [fetchEmployees, fetchModules]);

  const onChangeEmployeeId = useCallback((e) => {
    const value = e.target.value;
    setEmployeeId(value);
    setEmployeeIdError(value.trim() === "");
    if (onClearError) onClearError();
  }, [onClearError]);

  const onChangeStartDate = useCallback((e) => {
    const value = e.target.value;
    setAssignedAt(value);
    setStartDateError(value === "");
    if (onClearError) onClearError();
  }, [onClearError]);

  const onChangeProjectExperience = useCallback((e) => {
    const value = e.target.value;
    setProjectExperience(value);
    setProjectExperienceError(
      value === "" || isNaN(value) || Number(value) < 0,
    );
  }, []);

  const onChangeBillingAmount = useCallback((e) => {
    const value = e.target.value;
    setBillingAmount(value);
    setBillingAmountError(value === "" || isNaN(value) || Number(value) <= 0);
  }, []);

  const onChangeBillingType = useCallback((e) => {
    const value = e.target.value;
    setBillingType(value);
    setBillingTypeError(value.trim() === "");
  }, []);
    const onChangeServiceType = useCallback((e) => {
    const value = e.target.value;
    setServiceType(value);
    setServiceTypeError(value.trim() === "");
  }, []);

  const handleModuleChange = useCallback((newValues) => {
    setModuleIds(newValues);
  }, []);

  const validateForm = useCallback(() => {
    let hasError = false;

    if (employeeId.trim() === "") {
      setEmployeeIdError(true);
      hasError = true;
    }
    if (assignedAt === "") {
      setStartDateError(true);
      hasError = true;
    }
    if (
      projectExperience === "" ||
      isNaN(projectExperience) ||
      Number(projectExperience) < 0
    ) {
      setProjectExperienceError(true);
      hasError = true;
    }
    if (
      billingAmount === "" ||
      isNaN(billingAmount) ||
      Number(billingAmount) <= 0
    ) {
      setBillingAmountError(true);
      hasError = true;
    }
    if (billingType.trim() === "") {
      setBillingTypeError(true);
      hasError = true;
    }
    if (serviceType.trim() === "") {
      setServiceTypeError(true);
      hasError = true;
    }

    return !hasError;
  }, [employeeId, assignedAt, projectExperience, billingAmount, billingType, serviceType]);

  const onClickSubmit = useCallback(() => {
    if (validateForm()) {
      const assignmentObj = {
        employeeId: employeeId,
        assignedAt: assignedAt,
        projectExperience: Number(projectExperience),
        billingAmount: Number(billingAmount),
        billingType: billingType,
        module_ids: moduleIds.length > 0 ? moduleIds : null,
        project_id: selectedProjectId,
        serviceType: serviceType,
      };

      if (isEditMode && editData) {
        assignmentObj.id = editData.id;
      }

      onSubmit(assignmentObj);
    }
  }, [
    validateForm,
    employeeId,
    assignedAt,
    projectExperience,
    billingAmount,
    billingType,
    moduleIds,
    selectedProjectId,
    isEditMode,
    editData,
    onSubmit,
    serviceType
  ]);

  const errorInputStyle = useMemo(() => ({ borderLeft: "10px solid red" }), []);

  return (
    <div className="add-user-form">
      <div className="d-flex gap-3 flex-column ">
        <div className="d-flex gap-3">
          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={employeeIdError ? errorInputStyle : {}}
                value={employeeId}
                name="employeeId"
                onChange={onChangeEmployeeId}
                disabled={isEditMode}
              >
                <option value="">---Select Employee Name---</option>
                {empId &&
                  Array.isArray(empId) &&
                  empId
                    .sort((a, b) => a.full_name.localeCompare(b.full_name))
                    .map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.full_name}
                      </option>
                    ))}
              </StyledSelect>
              <StyledLabel className="user-label" hasValue={isEditMode}>Employee Name*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>
          <StyledSelectGroup>
            <StyledSelectContainer style={{ position: "relative" }}>
              <StyledLabel
                className="user-label"
                style={{
                  transform: "translateY(-25%) scale(0.8)",
                  backgroundColor: "#ffffff",
                  padding: "0 0.2em",
                  color: "#233747",
                }}
              >
                Modules
              </StyledLabel>
              <CustomMultiSelect
                options={modules}
                selectedValues={moduleIds}
                onChange={handleModuleChange}
                placeholder="---Select Module Name---"
                idKey="id"
                nameKey="name"
                showSearch={true}
                noOptionsMessage="No Modules Available"
              />
            </StyledSelectContainer>
          </StyledSelectGroup>
        </div>

        <div className="d-flex gap-3">
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={projectExperienceError ? errorInputStyle : {}}
                value={projectExperience}
                name="projectExperience"
                onChange={onChangeProjectExperience}
                type="number"
                min="0"
                step="0.1"
                placeholder="Enter Client Project Experience"
              />
              <StyledLabel className="user-label">
                Client Project Experience (Years)*
              </StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={startDateError ? errorInputStyle : {}}
                value={assignedAt}
                name="assignedAt"
                onChange={onChangeStartDate}
                type="date"
                min={projectStartDate}
                max="9999-12-31"
              />
              <StyledLabel className="user-label">Onboarding Date*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
          
        </div>
        <div className="d-flex gap-3">
         <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={serviceTypeError ? errorInputStyle : {}}
                value={serviceType}
                name="serviceType"
                onChange={onChangeServiceType}
              >
                <option value="">---Select Service Type---</option>
                <option value="Frontend Development Service">Frontend Development</option>
                <option value="Backend Development Service">Backend Development</option>
                <option value="Full Stack Development Service">Full Stack Development</option>
                <option value="QA & Testing Service">QA & Testing</option>
                <option value="Manual Testing Service">Manual Testing</option>
                <option value="Automation Testing Service">Automation Testing</option>
                <option value="UI/UX Design Service">UI/UX Design</option>


              </StyledSelect>
              <StyledLabel className="user-label">Service Type*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>
          
        </div>
        <h5 className="billing-details-title">Billing Details</h5>
        <div className="d-flex gap-3">
          <StyledSelectGroup>
            <StyledSelectContainer>
              <StyledSelect
                style={billingTypeError ? errorInputStyle : {}}
                value={billingType}
                name="billingType"
                onChange={onChangeBillingType}
              >
                <option value="">---Select Billing Type---</option>
                <option value="HOURLY">HOURLY</option>
                <option value="FIXED">FIXED</option>
              </StyledSelect>
              <StyledLabel className="user-label">Billing Type*</StyledLabel>
            </StyledSelectContainer>
          </StyledSelectGroup>
          <StyledInputGroup>
            <StyledInputContainer>
              <StyledInput
                style={billingAmountError ? errorInputStyle : {}}
                value={billingAmount}
                name="billingAmount"
                onChange={onChangeBillingAmount}
                type="number"
                step="0.01"
                placeholder="Enter Billing Amount"
              />
              <StyledLabel className="user-label">Billing Amount*</StyledLabel>
            </StyledInputContainer>
          </StyledInputGroup>
        </div>
      </div>
        {errorMsg && (
                <ErrorText className="mt-3">*{errorMsg}</ErrorText>
              )}

      <div className="add-user-submit-btn">
        <SecondaryButton type="button" onClick={onCancel}>
          Cancel
        </SecondaryButton>
        <PrimaryButton type="button" onClick={onClickSubmit}>
          {isLoading
            ? isEditMode
              ? "Updating..."
              : "Adding..."
            : isEditMode
            ? "Update"
            : "Add"}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default AddProjectAssignmentForm;
